import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FolderKanban, FileText, Settings, Plus, ArrowRight, 
  Image, Eye, EyeOff, Upload, Save, X, Edit, Mail, Phone, MapPin, Lock, User
} from 'lucide-react';
import api, { getApiUrl } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = getApiUrl();

const AdminDashboard = () => {
  const { user, login } = useAuth();
  const [stats, setStats] = useState({ projects: 0, services: 0, pages: 0 });
  const [settings, setSettings] = useState({
    logo_url: '',
    logo_visible: true,
    site_name: 'BMS',
    site_name_full: 'BrightMind Software',
    contact_email: 'info@brightmindsoftware.com',
    contact_phone: '+90 (212) 000 00 00',
    contact_address_tr: 'İstanbul, Türkiye',
    contact_address_en: 'Istanbul, Turkey'
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showPageModal, setShowPageModal] = useState(false);
  const [services, setServices] = useState([]);
  const [pages, setPages] = useState([]);
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
    new_username: ''
  });
  const [savingPassword, setSavingPassword] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [projectsRes, servicesRes, pagesRes, settingsRes] = await Promise.all([
        api.get('/projects'),
        api.get('/services'),
        api.get('/pages'),
        api.get('/settings')
      ]);
      setStats({
        projects: projectsRes.data.length,
        services: servicesRes.data.length,
        pages: pagesRes.data.length
      });
      setServices(servicesRes.data);
      setPages(pagesRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSettings(prev => ({ ...prev, logo_url: response.data.url }));
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Logo yüklenirken hata oluştu');
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/settings', settings);
      alert('Ayarlar kaydedildi');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Ayarlar kaydedilirken hata oluştu');
    } finally {
      setSavingSettings(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const savePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      alert('Yeni parolalar eşleşmiyor!');
      return;
    }
    if (passwordData.new_password.length < 6) {
      alert('Yeni parola en az 6 karakter olmalı!');
      return;
    }
    
    setSavingPassword(true);
    try {
      const response = await api.put('/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
        new_username: passwordData.new_username || null
      });
      
      // Update token if username changed
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
      }
      
      setPasswordData({ current_password: '', new_password: '', confirm_password: '', new_username: '' });
      alert('Hesap bilgileri güncellendi! ' + (passwordData.new_username ? 'Yeni kullanıcı adınız: ' + passwordData.new_username : ''));
    } catch (error) {
      console.error('Error changing password:', error);
      alert(error.response?.data?.detail || 'Parola değiştirirken hata oluştu');
    } finally {
      setSavingPassword(false);
    }
  };

  const cards = [
    { icon: FolderKanban, label: 'Projeler', count: stats.projects, path: '/admin/projects', color: 'blue' },
    { icon: Settings, label: 'Hizmetler', count: stats.services, path: '/admin/services', color: 'purple' },
    { icon: FileText, label: 'Sayfalar', count: stats.pages, path: '/admin/pages', color: 'cyan' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2 text-bms-text">Dashboard</h1>
        <p className="text-bms-text-muted">BMS yönetim paneline hoş geldiniz</p>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-3 gap-6 mb-12">
        {cards.map((card, index) => (
          <motion.div
            key={card.path}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Link
              to={card.path}
              className="block p-6 rounded-xl bg-white border border-gray-100 shadow-corporate hover:shadow-corporate-lg hover:border-bms-primary transition-all group"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center`}>
                  <card.icon className={`w-6 h-6 text-bms-primary`} />
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-bms-primary group-hover:translate-x-1 transition-all" />
              </div>
              <div className="text-4xl font-bold mb-1 text-bms-text">{card.count}</div>
              <div className="text-bms-text-muted">{card.label}</div>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Logo & Site Settings */}
      <div className="mb-12">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-bms-text">
          <Image className="w-5 h-5" />
          Logo & Site Ayarları
        </h2>
        <div className="p-6 rounded-xl bg-white border border-gray-100 shadow-corporate">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Logo Preview */}
            <div>
              <label className="block text-sm text-bms-text-muted mb-3">Logo Önizleme</label>
              <div className="p-6 rounded-xl bg-gray-50 border border-gray-200 flex items-center justify-center min-h-[120px]">
                {settings.logo_url ? (
                  <div className="relative">
                    <img 
                      src={settings.logo_url.startsWith('http') ? settings.logo_url : `${API_URL}${settings.logo_url}`}
                      alt="Logo"
                      className="max-h-20 object-contain"
                    />
                    <button
                      onClick={() => setSettings(prev => ({ ...prev, logo_url: '' }))}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="text-bms-text-muted text-center">
                    <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Logo yüklenmemiş</p>
                  </div>
                )}
              </div>
              
              {/* Upload Button */}
              <label className="mt-4 flex items-center gap-3 px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 cursor-pointer hover:border-bms-primary transition-colors">
                <Upload className="w-5 h-5 text-bms-text-muted" />
                <span className="text-bms-text-muted">Logo Yükle</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
              </label>
            </div>

            {/* Logo Settings */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-bms-text-muted mb-2">Logo Görünürlüğü</label>
                <button
                  onClick={() => setSettings(prev => ({ ...prev, logo_visible: !prev.logo_visible }))}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg w-full transition-all ${
                    settings.logo_visible 
                      ? 'bg-green-50 border border-green-200 text-green-700' 
                      : 'bg-gray-50 border border-gray-200 text-bms-text-muted'
                  }`}
                >
                  {settings.logo_visible ? (
                    <>
                      <Eye className="w-5 h-5" />
                      <span>Logo Görünür</span>
                    </>
                  ) : (
                    <>
                      <EyeOff className="w-5 h-5" />
                      <span>Logo Gizli</span>
                    </>
                  )}
                </button>
              </div>

              <div>
                <label className="block text-sm text-bms-text-muted mb-2">Site Adı (Kısa)</label>
                <input
                  type="text"
                  value={settings.site_name || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, site_name: e.target.value }))}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 
                           focus:border-bms-primary focus:ring-2 focus:ring-bms-primary/20 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm text-bms-text-muted mb-2">Site Adı (Tam)</label>
                <input
                  type="text"
                  value={settings.site_name_full || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, site_name_full: e.target.value }))}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 
                           focus:border-bms-primary focus:ring-2 focus:ring-bms-primary/20 outline-none transition-all"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Info Management */}
      <div className="mb-12">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-bms-text">
          <Mail className="w-5 h-5" />
          İletişim Bilgileri
        </h2>
        <div className="p-6 rounded-xl bg-white border border-gray-100 shadow-corporate">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm text-bms-text-muted mb-2 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                E-posta
              </label>
              <input
                type="email"
                value={settings.contact_email || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, contact_email: e.target.value }))}
                placeholder="info@example.com"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 
                         focus:border-bms-primary focus:ring-2 focus:ring-bms-primary/20 outline-none transition-all"
              />
            </div>

            <div>
              <label className="block text-sm text-bms-text-muted mb-2 flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Telefon
              </label>
              <input
                type="text"
                value={settings.contact_phone || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, contact_phone: e.target.value }))}
                placeholder="+90 (212) 000 00 00"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 
                         focus:border-bms-primary focus:ring-2 focus:ring-bms-primary/20 outline-none transition-all"
              />
            </div>

            <div>
              <label className="block text-sm text-bms-text-muted mb-2 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Adres (Türkçe)
              </label>
              <input
                type="text"
                value={settings.contact_address_tr || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, contact_address_tr: e.target.value }))}
                placeholder="İstanbul, Türkiye"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 
                         focus:border-bms-primary focus:ring-2 focus:ring-bms-primary/20 outline-none transition-all"
              />
            </div>

            <div>
              <label className="block text-sm text-bms-text-muted mb-2 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Address (English)
              </label>
              <input
                type="text"
                value={settings.contact_address_en || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, contact_address_en: e.target.value }))}
                placeholder="Istanbul, Turkey"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 
                         focus:border-bms-primary focus:ring-2 focus:ring-bms-primary/20 outline-none transition-all"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Save All Settings Button */}
      <div className="mb-12">
        <motion.button
          onClick={saveSettings}
          disabled={savingSettings}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full md:w-auto px-8 py-4 bg-bms-primary hover:bg-bms-primary-dark text-white rounded-lg 
                   font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
        >
          <Save className="w-5 h-5" />
          {savingSettings ? 'Kaydediliyor...' : 'Tüm Ayarları Kaydet'}
        </motion.button>
      </div>

      {/* Admin Account Settings */}
      <div className="mb-12">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-bms-text">
          <Lock className="w-5 h-5" />
          Hesap Ayarları
        </h2>
        <div className="p-6 rounded-xl bg-white border border-gray-100 shadow-corporate">
          <p className="text-sm text-bms-text-muted mb-6">
            Mevcut kullanıcı: <span className="font-semibold text-bms-text">{user?.username}</span>
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-bms-text mb-2 flex items-center gap-2">
                <User className="w-4 h-4" />
                Yeni Kullanıcı Adı (opsiyonel)
              </label>
              <input
                type="text"
                name="new_username"
                value={passwordData.new_username}
                onChange={handlePasswordChange}
                placeholder="Değiştirmek istemiyorsanız boş bırakın"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-bms-primary focus:ring-2 focus:ring-bms-primary/20 outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-bms-text mb-2 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Mevcut Parola *
              </label>
              <input
                type="password"
                name="current_password"
                value={passwordData.current_password}
                onChange={handlePasswordChange}
                placeholder="••••••••"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-bms-primary focus:ring-2 focus:ring-bms-primary/20 outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-bms-text mb-2">Yeni Parola *</label>
              <input
                type="password"
                name="new_password"
                value={passwordData.new_password}
                onChange={handlePasswordChange}
                placeholder="••••••••"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-bms-primary focus:ring-2 focus:ring-bms-primary/20 outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-bms-text mb-2">Yeni Parola (Tekrar) *</label>
              <input
                type="password"
                name="confirm_password"
                value={passwordData.confirm_password}
                onChange={handlePasswordChange}
                placeholder="••••••••"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-bms-primary focus:ring-2 focus:ring-bms-primary/20 outline-none transition-all"
              />
            </div>
          </div>
          <div className="mt-6">
            <button
              onClick={savePassword}
              disabled={savingPassword || !passwordData.current_password || !passwordData.new_password}
              className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold flex items-center gap-2 disabled:opacity-50 transition-all"
            >
              <Lock className="w-4 h-4" />
              {savingPassword ? 'Güncelleniyor...' : 'Hesap Bilgilerini Güncelle'}
            </button>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-xl font-bold mb-4 text-bms-text">Hızlı İşlemler</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link
            to="/admin/projects/new"
            className="flex items-center gap-3 p-4 rounded-lg bg-white border border-gray-100 shadow-corporate hover:border-bms-primary transition-all"
          >
            <Plus className="w-5 h-5 text-bms-primary" />
            <span className="text-bms-text">Yeni Proje Ekle</span>
          </Link>
          <button
            onClick={() => setShowServiceModal(true)}
            className="flex items-center gap-3 p-4 rounded-lg bg-white border border-gray-100 shadow-corporate hover:border-bms-primary transition-all text-left"
          >
            <Plus className="w-5 h-5 text-bms-primary" />
            <span className="text-bms-text">Yeni Hizmet Ekle</span>
          </button>
          <button
            onClick={() => setShowPageModal(true)}
            className="flex items-center gap-3 p-4 rounded-lg bg-white border border-gray-100 shadow-corporate hover:border-bms-primary transition-all text-left"
          >
            <Plus className="w-5 h-5 text-bms-primary" />
            <span className="text-bms-text">Yeni Sayfa Ekle</span>
          </button>
          <Link
            to="/admin/services"
            className="flex items-center gap-3 p-4 rounded-lg bg-white border border-gray-100 shadow-corporate hover:border-bms-primary transition-all"
          >
            <Edit className="w-5 h-5 text-bms-primary" />
            <span className="text-bms-text">Hizmetleri Düzenle</span>
          </Link>
        </div>
      </div>

      {/* Recent Services & Pages */}
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <h2 className="text-xl font-bold mb-4 flex items-center justify-between text-bms-text">
            <span>Hizmetler</span>
            <Link to="/admin/services" className="text-sm text-bms-primary hover:text-bms-primary-dark">
              Tümünü Gör
            </Link>
          </h2>
          <div className="space-y-2">
            {services.slice(0, 4).map((service) => (
              <div key={service.id} className="flex items-center justify-between p-3 rounded-xl bg-white border border-gray-100">
                <span className="text-sm text-bms-text">{service.title_tr}</span>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  service.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                }`}>
                  {service.active ? 'Aktif' : 'Pasif'}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-xl font-bold mb-4 flex items-center justify-between text-bms-text">
            <span>Sayfalar</span>
            <Link to="/admin/pages" className="text-sm text-bms-primary hover:text-bms-primary-dark">
              Tümünü Gör
            </Link>
          </h2>
          <div className="space-y-2">
            {pages.map((page) => (
              <div key={page.id} className="flex items-center justify-between p-3 rounded-xl bg-white border border-gray-100">
                <span className="text-sm text-bms-text">{page.title_tr}</span>
                <span className="text-xs text-bms-text-muted">/{page.slug}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Service Modal */}
      {showServiceModal && (
        <ServiceModal onClose={() => { setShowServiceModal(false); fetchData(); }} />
      )}

      {/* Page Modal */}
      {showPageModal && (
        <PageModal onClose={() => { setShowPageModal(false); fetchData(); }} />
      )}
    </div>
  );
};

// Service Modal Component
const ServiceModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    slug: '',
    title_tr: '',
    title_en: '',
    short_desc_tr: '',
    short_desc_en: '',
    content_tr: '',
    content_en: '',
    icon: 'Code',
    order: 0,
    active: true
  });
  const [saving, setSaving] = useState(false);

  const iconOptions = ['Code', 'Brain', 'Globe', 'Database', 'Settings', 'Cpu', 'Shield', 'Zap', 'Layers', 'Cloud'];

  const generateSlug = () => {
    const slug = formData.title_tr
      .toLowerCase()
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    setFormData(prev => ({ ...prev, slug }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/services', formData);
      onClose();
    } catch (error) {
      console.error('Error creating service:', error);
      alert(error.response?.data?.detail || 'Hizmet oluşturulurken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gray-900 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Yeni Hizmet Ekle</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Başlık (TR) *</label>
              <input
                type="text"
                value={formData.title_tr}
                onChange={(e) => setFormData(prev => ({ ...prev, title_tr: e.target.value }))}
                onBlur={() => !formData.slug && generateSlug()}
                required
                className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Title (EN) *</label>
              <input
                type="text"
                value={formData.title_en}
                onChange={(e) => setFormData(prev => ({ ...prev, title_en: e.target.value }))}
                required
                className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">URL Slug *</label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                required
                className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">İkon</label>
              <select
                value={formData.icon}
                onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-blue-500 focus:outline-none"
              >
                {iconOptions.map(icon => (
                  <option key={icon} value={icon}>{icon}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Kısa Açıklama (TR) *</label>
              <textarea
                value={formData.short_desc_tr}
                onChange={(e) => setFormData(prev => ({ ...prev, short_desc_tr: e.target.value }))}
                required
                rows={2}
                className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-blue-500 focus:outline-none resize-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Short Desc (EN) *</label>
              <textarea
                value={formData.short_desc_en}
                onChange={(e) => setFormData(prev => ({ ...prev, short_desc_en: e.target.value }))}
                required
                rows={2}
                className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-blue-500 focus:outline-none resize-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Detaylı İçerik (TR) - HTML *</label>
            <textarea
              value={formData.content_tr}
              onChange={(e) => setFormData(prev => ({ ...prev, content_tr: e.target.value }))}
              required
              rows={4}
              className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-blue-500 focus:outline-none resize-none font-mono text-sm"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Detail Content (EN) - HTML *</label>
            <textarea
              value={formData.content_en}
              onChange={(e) => setFormData(prev => ({ ...prev, content_en: e.target.value }))}
              required
              rows={4}
              className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-blue-500 focus:outline-none resize-none font-mono text-sm"
            />
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm">Aktif</span>
            </label>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-400">Sıra:</label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                className="w-20 px-3 py-1 rounded-lg bg-white/5 border border-white/10 focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 rounded-xl glass hover:bg-white/10"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 font-semibold disabled:opacity-50"
            >
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Page Modal Component
const PageModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    slug: '',
    title_tr: '',
    title_en: '',
    content_tr: '',
    content_en: '',
    meta_desc_tr: '',
    meta_desc_en: ''
  });
  const [saving, setSaving] = useState(false);

  const generateSlug = () => {
    const slug = formData.title_tr
      .toLowerCase()
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    setFormData(prev => ({ ...prev, slug }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/pages', formData);
      onClose();
    } catch (error) {
      console.error('Error creating page:', error);
      alert(error.response?.data?.detail || 'Sayfa oluşturulurken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gray-900 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Yeni Sayfa Ekle</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Başlık (TR) *</label>
              <input
                type="text"
                value={formData.title_tr}
                onChange={(e) => setFormData(prev => ({ ...prev, title_tr: e.target.value }))}
                onBlur={() => !formData.slug && generateSlug()}
                required
                className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Title (EN) *</label>
              <input
                type="text"
                value={formData.title_en}
                onChange={(e) => setFormData(prev => ({ ...prev, title_en: e.target.value }))}
                required
                className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">URL Slug *</label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
              required
              className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-blue-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">İçerik (TR) - HTML *</label>
            <textarea
              value={formData.content_tr}
              onChange={(e) => setFormData(prev => ({ ...prev, content_tr: e.target.value }))}
              required
              rows={6}
              className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-blue-500 focus:outline-none resize-none font-mono text-sm"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Content (EN) - HTML *</label>
            <textarea
              value={formData.content_en}
              onChange={(e) => setFormData(prev => ({ ...prev, content_en: e.target.value }))}
              required
              rows={6}
              className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-blue-500 focus:outline-none resize-none font-mono text-sm"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Meta Açıklama (TR)</label>
              <textarea
                value={formData.meta_desc_tr}
                onChange={(e) => setFormData(prev => ({ ...prev, meta_desc_tr: e.target.value }))}
                rows={2}
                className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-blue-500 focus:outline-none resize-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Meta Description (EN)</label>
              <textarea
                value={formData.meta_desc_en}
                onChange={(e) => setFormData(prev => ({ ...prev, meta_desc_en: e.target.value }))}
                rows={2}
                className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-blue-500 focus:outline-none resize-none"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 rounded-xl glass hover:bg-white/10"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 font-semibold disabled:opacity-50"
            >
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AdminDashboard;
